import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Modal, Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../../services/api-service.js";
import { getEnabledFieldApi } from "../Tickets/Ticketapi.js";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { ReactComponent as ModalCloseIcon } from "../../Assets/svg/close-modal.svg";
import { ReactComponent as ArrowDownIcon } from "../../Assets/svg/arrow-down.svg";
import "./Topbar.css";

const QuickTicketModal = ({ onHide, onSuccess }) => {
  const navigate = useNavigate();
  const [priority, setPriority] = useState("High");
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedReporter, setSelectedReporter] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [lastAssigneeId, setLastAssigneeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fieldLabels, setFieldLabels] = useState({});

  const getFieldLabel = (fieldKey, defaultLabel = fieldKey) => {
    let custom = fieldLabels?.[fieldKey];
    if ((!custom || typeof custom !== "string" || custom.trim() === "") && (fieldKey === "Assign To" || fieldKey === "Assigned To")) {
      custom = fieldLabels?.["Assign To"] || fieldLabels?.["Assigned To"];
    }
    if (custom && typeof custom === "string" && custom.trim() !== "") {
      return custom.trim();
    }
    return defaultLabel;
  };

  const renderFieldLabel = (fieldKey, defaultLabel, isMandatory = true) => {
    const labelText = getFieldLabel(fieldKey, defaultLabel);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          verticalAlign: "middle",
        }}
      >
        <span
          title={labelText}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {labelText}
        </span>
        {isMandatory && <span style={{ color: "red", marginLeft: "3px", flexShrink: 0 }}> *</span>}
      </span>
    );
  };

  useEffect(() => {
    const fetchConfiguredFields = async () => {
      try {
        const response = await getEnabledFieldApi();
        if (response?.success === true) {
          setFieldLabels(response?.application_labels ?? response?.labels ?? {});
        }
      } catch (error) {
        console.error("Error fetching configured fields:", error);
      }
    };
    fetchConfiguredFields();
  }, []);

  useEffect(() => {
    const fetchLastTicketData = async () => {
      try {
        const response = await apiCall("get", "get_last_ticket_data");
        if (response?.status === 200 && response.data?.success && response.data.data) {
          const lastData = response.data.data;
          if (lastData.project_id) {
            setSelectedProject({ value: lastData.project_id, label: lastData.project });
          }
          if (lastData.assignee_id) {
            setSelectedAssignee({ value: lastData.assignee_id, label: lastData.assignee });
            setLastAssigneeId(lastData.assignee_id);
          }
          if (lastData.reporter_id) {
            setSelectedReporter({ value: lastData.reporter_id, label: lastData.reporter });
          }
          if (lastData.team_id) {
            setSelectedTeam({ value: lastData.team_id, label: lastData.team });
          }
          if (lastData.priority) {
            setPriority(lastData.priority);
          }
        }
      } catch (error) {
        console.error("Error fetching last ticket data:", error);
      }
    };
    fetchLastTicketData();
  }, []);

  const handleSave = async () => {
    const newErrors = {};
    if (!title?.trim()) newErrors.title = `${getFieldLabel("Title", "Title")} is required`;
    if (!description?.trim()) newErrors.description = `${getFieldLabel("Description", "Description")} is required`;
    if (!selectedProject) newErrors.project = `${getFieldLabel("Project", "Project")} is required`;
    if (!selectedAssignee) newErrors.assignee = `${getFieldLabel("Assign To", "Assignee")} is required`;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setLoading(true);
    try {
      const body = {
        title: title,
        description: description,
        project_id: selectedProject?.value ?? "",
        assign_to: selectedAssignee?.value ?? "",
        reporter_id: selectedAssignee?.value === lastAssigneeId ? (selectedReporter?.value ?? "") : "",
        team_id: selectedAssignee?.value === lastAssigneeId ? (selectedTeam?.value ?? "") : "",
        priority: priority,
      };
      const response = await apiCall("post", "ticket/quick_create", body);
      if (response?.status === 200 && response.data?.success === true) {
        onSuccess?.(response.data);
      }
    } catch (error) {
      console.error("Error creating quick ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoreDetails = () => {
    const isSameAssignee = selectedAssignee?.value === lastAssigneeId;
    navigate("/ticket/add", {
      state: {
        quickData: {
          title,
          description,
          project: selectedProject,
          assignee: selectedAssignee,
          reporter: isSameAssignee ? selectedReporter : null,
          team: isSameAssignee ? selectedTeam : null,
          priority: priority,
        },
      },
    });
    onHide();
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    bgcolor: "background.paper",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "16px",
    p: "24px",
    outline: "none",
  };

  const getPriorityColor = (p) => {
    switch (p) {
      case "High": return "#F44336";
      case "Medium": return "#FFC107";
      case "Low": return "#4CAF50";
      default: return "#2C8C53";
    }
  };

  return (
    <Modal open={true} onClose={onHide}>
      <Box sx={modalStyle}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontFamily: "Satoshi", fontSize: "24px", fontWeight: "700", margin: 0 }}>Quick Ticket</h2>
          <div className="d-flex align-items-center gap-3">
            <div style={{ position: "relative" }}>
              <div 
                onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px", 
                  background: "#F1F5F9", 
                  padding: "6px 12px", 
                  borderRadius: "8px", 
                  cursor: "pointer",
                  userSelect: "none"
                }}
              >
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: getPriorityColor(priority) }}></span>
                <span style={{ fontSize: "14px", fontWeight: "500", color: "#1E293B", fontFamily: "Satoshi" }}>{priority}</span>
                <ArrowDownIcon style={{ width: "12px", height: "12px", transform: showPriorityDropdown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </div>
              
              {showPriorityDropdown && (
                <div style={{ 
                  position: "absolute", 
                  top: "100%", 
                  right: 0, 
                  marginTop: "4px", 
                  background: "#FFFFFF", 
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", 
                  borderRadius: "8px", 
                  zIndex: 10,
                  width: "120px",
                  overflow: "hidden"
                }}>
                  {["High", "Medium", "Low"].map((p) => (
                    <div 
                      key={p}
                      onClick={() => {
                        setPriority(p);
                        setShowPriorityDropdown(false);
                      }}
                      style={{ 
                        padding: "8px 12px", 
                        fontSize: "14px", 
                        cursor: "pointer", 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "8px",
                        background: priority === p ? "#F8FAFC" : "transparent"
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#F1F5F9"}
                      onMouseLeave={(e) => e.target.style.background = priority === p ? "#F8FAFC" : "transparent"}
                    >
                      <span style={{ width: "8px", height: "8px", borderRadius: "3px", backgroundColor: getPriorityColor(p) }}></span>
                      {p}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <IconButton onClick={onHide} size="small" aria-label="close">
              <ModalCloseIcon />
            </IconButton>
          </div>
        </div>

        <div className="mb-3">
          <label className="quick-modal-label" htmlFor="quick-ticket-title">
            {renderFieldLabel("Title", "Title")}
          </label>
          <input
            id="quick-ticket-title"
            type="text"
            className="quick-modal-input"
            placeholder="Enter here..."
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors({ ...errors, title: null });
            }}
            style={{ borderColor: errors.title ? "red" : undefined }}
          />
          {errors.title && <div style={{ color: "red", fontSize: "12px", marginTop: "4px", fontFamily: "Satoshi" }}>{errors.title}</div>}
        </div>

        <div className="mb-3">
          <label className="quick-modal-label" htmlFor="quick-ticket-desc">
            {renderFieldLabel("Description", "Description")}
          </label>
          <textarea
            id="quick-ticket-desc"
            className="quick-modal-textarea"
            placeholder="Enter here..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description) setErrors({ ...errors, description: null });
            }}
            style={{ borderColor: errors.description ? "red" : undefined }}
          />
          {errors.description && <div style={{ color: "red", fontSize: "12px", marginTop: "4px", fontFamily: "Satoshi" }}>{errors.description}</div>}
        </div>

        <div className="row mb-4">
          <div className="col-6">
            <label className="quick-modal-label">
              {renderFieldLabel("Project", "Project")}
            </label>
            <SearchWithAPI
              apiUrl="get_project"
              placeholder="Select"
              selectedOption={selectedProject}
              onDropdownChange={(val) => {
                setSelectedProject(val);
                if (errors.project) setErrors({ ...errors, project: null });
              }}
            />
            {errors.project && <div style={{ color: "red", fontSize: "12px", marginTop: "4px", fontFamily: "Satoshi" }}>{errors.project}</div>}
          </div>
          <div className="col-6">
            <label className="quick-modal-label">
              {renderFieldLabel("Assign To", "Assigned To")}
            </label>
            <SearchWithAPI
              apiUrl="get_employee"
              placeholder="Select"
              selectedOption={selectedAssignee}
              onDropdownChange={(val) => {
                setSelectedAssignee(val);
                if (errors.assignee) setErrors({ ...errors, assignee: null });
              }}
              showImage={true}
            />
            {errors.assignee && <div style={{ color: "red", fontSize: "12px", marginTop: "4px", fontFamily: "Satoshi" }}>{errors.assignee}</div>}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <div 
            className="quick-more-details" 
            onClick={handleMoreDetails} 
            role="button" 
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleMoreDetails()}
          >
            More details <ArrowDownIcon style={{ marginLeft: "8px", transform: "rotate(-90deg)" }} />
          </div>
          <div className="d-flex gap-3">
            <button className="quick-cancel-btn" onClick={onHide}>Cancel</button>
            <button className="quick-save-btn" onClick={handleSave} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" variant="light" /> : "Save & Start Progress"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default QuickTicketModal;
